import {
  afterNextRender,
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { selectAuth } from '@app/features/auth/auth.selectors';
import {
  APP_NAVIGATION_CONTEXT_DESCRIPTORS,
  AppNavigationContext,
  AppNavigationContextDescriptor,
  hasRequiredAccess,
} from '@app/core/models/app-navigation.model';
import { PvsShiftStore } from '@app/features/pvs-shift/pvs-shift.store';
import {
  LucideClipboardList,
  LucideClock4,
  LucideDynamicIcon,
  LucideIconInput,
  LucideLayoutDashboard,
  LucideMap,
  LucideMessageCircle,
  LucideSquareMenu,
} from '@lucide/angular';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AppFocusTargetDirective } from '@shared/directives/app-focus-target.directive';
import { ChatDaemon } from 'ias-ppr-mt-chat-client';

interface MenuTabPresentation {
  readonly labelKey: string;
  readonly icon: LucideIconInput;
}

export type MenuTabDescriptor = AppNavigationContextDescriptor &
  MenuTabPresentation;

interface MenuTabBadgeView {
  readonly label: string;
  readonly muted: boolean;
  readonly ariaLabel: string | null;
}

const SCROLL_EDGE_TOLERANCE_PX = 1;

const MENU_TAB_PRESENTATION = {
  dashboard: {
    labelKey: 'MENU_TABS.DASHBOARD',
    icon: LucideLayoutDashboard,
  },
  tasks: {
    labelKey: 'MENU_TABS.TASKS',
    icon: LucideClipboardList,
  },
  'pvs-shift': {
    labelKey: 'MENU_TABS.PVS_SHIFT',
    icon: LucideClock4,
  },
  events: {
    labelKey: 'MENU_TABS.EVENTS',
    icon: LucideSquareMenu,
  },
  map: {
    labelKey: 'MENU_TABS.MAP',
    icon: LucideMap,
  },
  analytics: {
    labelKey: 'SCENARIO_MODELING.TITLE',
    icon: LucideLayoutDashboard,
  },
  chat: {
    labelKey: 'MENU_TABS.CHAT',
    icon: LucideMessageCircle,
  },
} as const satisfies Readonly<
  Record<AppNavigationContext, MenuTabPresentation>
>;

export const MENU_TAB_DESCRIPTORS: readonly MenuTabDescriptor[] =
  APP_NAVIGATION_CONTEXT_DESCRIPTORS.map((descriptor) => ({
    ...descriptor,
    ...MENU_TAB_PRESENTATION[descriptor.context],
  }));

@Component({
  selector: 'app-menu-tabs',
  imports: [
    TranslatePipe,
    RouterLink,
    LucideDynamicIcon,
    AppFocusTargetDirective,
  ],
  templateUrl: './menu-tabs.component.html',
  styleUrl: './menu-tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuTabsComponent {
  private readonly store = inject(Store);
  private readonly chatDaemon = inject(ChatDaemon);
  private readonly pvsShiftStore = inject(PvsShiftStore);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly activeContext = input.required<AppNavigationContext>();

  protected readonly tabs = computed((): readonly MenuTabDescriptor[] => {
    const authState = this.authState();

    if (!authState.init || authState.accessStatus !== 'ready') {
      return [];
    }

    return MENU_TAB_DESCRIPTORS.filter(({ access }) =>
      hasRequiredAccess(authState.access, access),
    );
  });

  protected readonly badges = computed(
    (): Partial<Record<AppNavigationContext, MenuTabBadgeView>> => {
      const language = this.translate.currentLang() ?? '';
      const chatUnreadCount = this.chatDaemon.unreadCount();
      const mutedChatUnreadCount = this.chatDaemon.mutedUnreadCount();
      const unmutedChatUnreadCount = this.chatDaemon.unmutedUnreadCount();
      const actionableShiftCount = this.pvsShiftStore.actionableCount();
      const badges: Partial<Record<AppNavigationContext, MenuTabBadgeView>> =
        {};

      if (chatUnreadCount > 0) {
        const label = formatMenuBadgeCount(chatUnreadCount);
        const muted = unmutedChatUnreadCount === 0 && mutedChatUnreadCount > 0;

        badges.chat = {
          label,
          muted,
          ariaLabel: this.chatAriaLabel(language, label, muted),
        };
      }

      if (actionableShiftCount > 0) {
        const label = formatMenuBadgeCount(actionableShiftCount);

        badges['pvs-shift'] = {
          label,
          muted: false,
          ariaLabel: this.shiftAriaLabel(language, label),
        };
      }

      return badges;
    },
  );
  protected readonly canScrollToStart = signal(false);
  protected readonly canScrollToEnd = signal(false);

  private readonly authState = this.store.selectSignal(selectAuth);

  private readonly menuViewport =
    viewChild<ElementRef<HTMLElement>>('menuViewport');
  private readonly menuTrack = viewChild<ElementRef<HTMLElement>>('menuTrack');
  private readonly menuItems =
    viewChildren<ElementRef<HTMLAnchorElement>>('menuItem');

  constructor() {
    afterNextRender(() => {
      const viewport = this.menuViewport()?.nativeElement;
      const track = this.menuTrack()?.nativeElement;

      if (!viewport || !track) {
        return;
      }

      untracked(() => {
        const observer = new ResizeObserver(() =>
          this.updateScrollEdges(viewport),
        );
        observer.observe(viewport);
        observer.observe(track);
        this.destroyRef.onDestroy(() => observer.disconnect());
        this.updateScrollEdges(viewport);
      });
    });

    afterRenderEffect(() => {
      const activeContext = this.activeContext();
      const tabs = this.tabs();
      const items = this.menuItems();
      const viewport = this.menuViewport()?.nativeElement;

      untracked(() => {
        this.updateScrollEdges(viewport);

        if (items.length !== tabs.length) {
          return;
        }

        const activeIndex = tabs.findIndex(
          (tab) => tab.context === activeContext,
        );

        if (activeIndex < 0) {
          return;
        }

        const activeItem = items[activeIndex];

        if (activeItem) {
          this.revealMenuItem(activeItem.nativeElement);
          this.updateScrollEdges(viewport);
        }
      });
    });
  }

  private chatAriaLabel(
    language: string,
    count: string,
    muted: boolean,
  ): string | null {
    if (!language) {
      return null;
    }

    return this.translate.instant(
      muted ? 'MENU_TABS.CHAT_UNREAD_MUTED' : 'MENU_TABS.CHAT_UNREAD',
      { count },
    );
  }

  private shiftAriaLabel(language: string, count: string): string | null {
    if (!language) {
      return null;
    }

    return this.translate.instant('MENU_TABS.PVS_SHIFT_ACTIONABLE', { count });
  }

  protected updateScrollEdges(viewport?: HTMLElement): void {
    if (!viewport) {
      this.canScrollToStart.set(false);
      this.canScrollToEnd.set(false);
      return;
    }

    const maximumScrollLeft = Math.max(
      0,
      viewport.scrollWidth - viewport.clientWidth,
    );

    this.canScrollToStart.set(viewport.scrollLeft > SCROLL_EDGE_TOLERANCE_PX);
    this.canScrollToEnd.set(
      maximumScrollLeft - viewport.scrollLeft > SCROLL_EDGE_TOLERANCE_PX,
    );
  }

  protected revealMenuItem(item: HTMLElement): void {
    item.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
      inline: 'nearest',
    });
  }
}

function formatMenuBadgeCount(count: number): string {
  return count > 99 ? '99+' : String(count);
}
